<script setup lang="ts">
  import { computed, nextTick, ref, watch } from "vue";
  import { Check, CircleCheck, Lock, Search, X } from "lucide-vue-next";

  import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
  import { Button } from "@/components/ui/button";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import type { PickerGroup, PickerItem } from "./types";

  const props = withDefaults(
    defineProps<{
      title: string;
      description: string;
      groups: PickerGroup[];
      items: PickerItem[];
      sections?: Partial<Record<string, string[]>>;
      actionLabel?: string;
      actionLabelFor?: (count: number) => string;
      multiple?: boolean;
      initialKey?: string;
      footnote?: string;
    }>(),
    {
      sections: () => ({}),
      actionLabel: "Select",
      actionLabelFor: undefined,
      multiple: false,
      initialKey: undefined,
      footnote: undefined,
    },
  );

  const emit = defineEmits<{ select: [keys: string | string[]] }>();

  const open = defineModel<boolean>("open", { default: false });

  interface PickerSection {
    label: string;
    items: PickerItem[];
  }

  const groups = computed(() =>
    props.groups
      .map((group) => ({ ...group, count: props.items.filter((item) => item.group === group.id).length }))
      .filter((group) => group.count > 0),
  );

  const group = ref<string>(props.groups[0]?.id ?? "");
  const query = ref("");
  const selectedKey = ref<string | null>(null);
  const picked = ref<string[]>([]);
  const isPicked = (item: PickerItem) => picked.value.includes(item.key);
  const togglePick = (item: PickerItem) => {
    if (item.locked) return;
    picked.value = isPicked(item) ? picked.value.filter((key) => key !== item.key) : [...picked.value, item.key];
  };
  const ctaLabel = computed(() => {
    if (!props.multiple) return props.actionLabel;
    return props.actionLabelFor?.(picked.value.length) ?? `${props.actionLabel} (${picked.value.length})`;
  });
  const ctaDisabled = computed(() =>
    props.multiple ? picked.value.length === 0 : !selected.value || selected.value.locked,
  );
  const searchInput = ref<HTMLInputElement | null>(null);
  const listEl = ref<HTMLElement | null>(null);

  const normalizedQuery = computed(() => query.value.trim().toLowerCase());
  const isSearching = computed(() => normalizedQuery.value.length > 0);
  const isGroupActive = (id: string) => !isSearching.value && group.value === id;

  const filtered = computed(() => {
    if (!isSearching.value) return props.items.filter((item) => item.group === group.value);
    return props.items.filter(
      (item) =>
        item.name.toLowerCase().includes(normalizedQuery.value) ||
        item.description.toLowerCase().includes(normalizedQuery.value) ||
        (item.searchText?.toLowerCase().includes(normalizedQuery.value) ?? false),
    );
  });

  const sections = computed<PickerSection[]>(() => {
    const order = isSearching.value
      ? props.groups.flatMap((g) => props.sections[g.id] ?? [])
      : (props.sections[group.value] ?? []);
    const labels = [...order, ...filtered.value.map((item) => item.section).filter((label) => !order.includes(label))];
    return [...new Set(labels)]
      .map((label) => ({ label, items: filtered.value.filter((item) => item.section === label) }))
      .filter((section) => section.items.length > 0);
  });

  const flat = computed(() => sections.value.flatMap((section) => section.items));
  const selected = computed(() => flat.value.find((item) => item.key === selectedKey.value) ?? flat.value[0] ?? null);

  const revealSelected = () =>
    nextTick(() =>
      listEl.value?.querySelector(`[data-picker-key="${selectedKey.value}"]`)?.scrollIntoView({ block: "nearest" }),
    );

  watch(flat, (list) => {
    if (!list.some((item) => item.key === selectedKey.value)) selectedKey.value = list[0]?.key ?? null;
  });

  watch(open, (isOpen) => {
    if (!isOpen) return;
    const initial = props.items.find((item) => item.key === props.initialKey);
    group.value = initial?.group ?? groups.value[0]?.id ?? props.groups[0]?.id ?? "";
    query.value = "";
    picked.value = [];
    selectedKey.value = initial?.key ?? flat.value[0]?.key ?? null;
    nextTick(() => {
      searchInput.value?.focus();
      if (initial) revealSelected();
    });
  });

  const selectGroup = (id: string) => {
    group.value = id;
    query.value = "";
    selectedKey.value = props.items.find((item) => item.group === id)?.key ?? null;
  };

  const moveSelection = (delta: number) => {
    const list = flat.value;
    if (list.length === 0) return;
    const index = list.findIndex((item) => item.key === selected.value?.key);
    selectedKey.value = list[Math.min(list.length - 1, Math.max(0, index + delta))].key;
    revealSelected();
  };

  const insert = (item: PickerItem | null = selected.value) => {
    if (props.multiple) {
      if (picked.value.length === 0) return;
      emit("select", picked.value);
    } else {
      if (!item || item.locked) return;
      emit("select", item.key);
    }
    open.value = false;
  };

  const onRowClick = (item: PickerItem) => (props.multiple ? togglePick(item) : insert(item));

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveSelection(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (props.multiple && !(event.metaKey || event.ctrlKey) && selected.value) togglePick(selected.value);
      else insert();
    } else if (event.key === "/" && event.target !== searchInput.value) {
      event.preventDefault();
      searchInput.value?.focus();
    }
  };
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent
      :show-close-button="false"
      class="grid h-[min(780px,calc(100vh-64px))] w-[min(1240px,calc(100vw-64px))] max-w-none grid-cols-[minmax(180px,256px)_minmax(260px,1fr)_minmax(240px,420px)] gap-0 overflow-hidden rounded-xl p-0 shadow-xl"
      @keydown="onKeydown">
      <DialogDescription class="sr-only">{{ description }}</DialogDescription>

      <aside class="flex min-h-0 flex-col gap-0.5 border-r bg-muted px-3 py-5">
        <DialogTitle class="heading-title px-2.5 pb-1.5">
          {{ title }}
        </DialogTitle>
        <div class="h-3.5" />
        <button
          v-for="g in groups"
          :key="g.id"
          type="button"
          class="flex items-center gap-2.5 rounded-lg border px-2.5 py-2.25 text-left transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          :class="isGroupActive(g.id) ? 'border-ring bg-white shadow-xs' : 'border-transparent'"
          @click="selectGroup(g.id)">
          <component
            :is="g.icon"
            class="size-4 shrink-0"
            :class="isGroupActive(g.id) ? 'text-primary' : 'text-muted-foreground'"
            :stroke-width="1.8" />
          <span class="flex min-w-0 flex-col">
            <span class="whitespace-nowrap text-13 font-semibold text-primary">{{ g.label }}</span>
            <span class="text-xs leading-snug text-muted-foreground">{{ g.description }}</span>
          </span>
          <span
            class="ml-auto text-2xs font-semibold"
            :class="isGroupActive(g.id) ? 'text-muted-foreground' : 'text-foreground-subtle'">
            {{ g.count }}
          </span>
        </button>
        <p
          v-if="footnote"
          class="mt-auto px-2.5 pt-3.5 pb-1.5 text-xs leading-normal text-foreground-subtle">
          {{ footnote }}
        </p>
      </aside>

      <section class="flex min-h-0 min-w-0 flex-col overflow-hidden border-r">
        <div class="px-4.5 pt-3.5 pb-2.5">
          <label class="flex h-8 items-center gap-2.25 rounded-lg border bg-muted px-3">
            <Search
              class="size-3.75 shrink-0 text-foreground-subtle"
              :stroke-width="2" />
            <input
              ref="searchInput"
              v-model="query"
              type="text"
              placeholder="Search everything…"
              class="min-w-0 flex-1 bg-transparent text-13 text-foreground outline-none placeholder:text-foreground-subtle" />
          </label>
        </div>
        <div
          ref="listEl"
          class="flex min-h-0 flex-col gap-px overflow-y-auto px-4.5 pb-4.5">
          <div
            v-for="section in sections"
            :key="section.label"
            class="flex flex-col gap-px pb-1">
            <p class="heading-label px-1 pt-2.25 pb-0.75">
              {{ section.label }}
            </p>
            <button
              v-for="item in section.items"
              :key="item.key"
              type="button"
              :title="item.description"
              :data-picker-key="item.key"
              class="flex items-baseline gap-2.25 rounded-md px-2.25 py-1.25 text-left transition-colors"
              :class="[
                item.key === selected?.key && 'shadow-[inset_0_0_0_1px_var(--ring)]',
                multiple && isPicked(item) ? 'bg-secondary' : item.key === selected?.key && 'bg-ring/10',
                item.locked ? 'cursor-not-allowed' : 'cursor-pointer',
              ]"
              @mouseenter="selectedKey = item.key"
              @click="onRowClick(item)">
              <component
                :is="item.icon"
                class="size-3.75 flex-none self-center"
                :class="item.key === selected?.key ? 'text-episto-teal-700' : 'text-foreground-secondary'"
                :stroke-width="1.8" />
              <span class="whitespace-nowrap text-13 font-medium text-primary">{{ item.name }}</span>
              <span class="min-w-0 flex-1 truncate text-xs text-foreground-subtle">{{ item.description }}</span>
              <span
                v-if="item.badge"
                class="ml-auto flex-none self-center rounded-full bg-secondary px-1.5 text-2xs text-muted-foreground">
                {{ item.badge }}
              </span>
              <CircleCheck
                v-if="multiple && isPicked(item)"
                class="ml-auto size-4 flex-none self-center text-episto-teal-700"
                :stroke-width="2" />
              <Check
                v-else-if="item.checked"
                class="ml-auto size-3.5 flex-none self-center"
                :class="multiple ? 'text-foreground-subtle' : 'text-episto-teal-700'"
                :stroke-width="2" />
              <Lock
                v-if="item.locked"
                class="size-3.25 flex-none self-center text-foreground-subtle"
                :class="item.badge || item.checked || (multiple && isPicked(item)) ? 'ml-1' : 'ml-auto'"
                :stroke-width="1.8" />
            </button>
          </div>
          <EmptyState
            v-if="sections.length === 0"
            :title="`No match for “${query}”`"
            description="Try another word, or browse a different category."
            size="lg" />
        </div>
      </section>

      <section class="flex min-h-0 min-w-0 flex-col bg-[image:var(--page-gradient)]">
        <div class="flex justify-end px-5 pt-4">
          <button
            type="button"
            aria-label="Close"
            class="grid size-7 place-items-center rounded-lg border bg-white text-muted-foreground transition-colors hover:text-primary"
            @click="open = false">
            <X
              class="size-3.75"
              :stroke-width="2" />
          </button>
        </div>
        <div
          v-if="selected"
          class="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-5 pt-3.5 pb-1">
          <div class="flex flex-col gap-1.5">
            <p class="heading-title flex items-center gap-2">
              <component
                :is="selected.icon"
                class="size-4 shrink-0 text-muted-foreground"
                :stroke-width="1.8" />
              <span class="min-w-0 truncate">{{ selected.name }}</span>
            </p>
            <p
              v-if="selected.about"
              class="text-13 leading-relaxed text-foreground-secondary [text-wrap:pretty]">
              {{ selected.about }}
            </p>
          </div>
          <div
            v-if="selected.lockedReason"
            class="rounded-lg border border-status-warning-border bg-status-warning-soft px-3 py-2 text-xs text-status-warning-strong">
            {{ selected.lockedReason }}
          </div>
          <div class="flex flex-col gap-3 rounded-xl border bg-white px-4 pt-4.5 pb-5 shadow-sm">
            <slot
              name="preview"
              :item="selected" />
          </div>
        </div>
        <div
          v-else
          class="flex-1" />
        <div class="flex justify-end gap-2.5 border-t bg-white px-5 py-3">
          <Button
            variant="outline"
            @click="open = false">
            Cancel
          </Button>
          <Button
            :disabled="ctaDisabled"
            class="shadow-md transition-transform hover:scale-104 hover:shadow-lg active:scale-97"
            @click="insert()">
            {{ ctaLabel }}
          </Button>
        </div>
      </section>
    </DialogContent>
  </Dialog>
</template>
